import React, { FormEvent, ReactNode, useState } from "react";

import {
	Badge,
	Button,
	Card,
	Input,
	Picto,
	Title,
	Grid,
	TruncatedTooltipText,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import ReportsServices from "@/services/ReportsService";

import { useReportDownload } from "@/hooks/useReportDownload";

import { FeatureGate } from "@/components/atoms";
import { PlanGate } from "@/components/organisms/PlanGate/PlanGate";

import { reportKeys } from "@/constants/CQueryKeys";

import { TSavedReport } from "@/types";

import { promiseToast } from "@/modules/index";
import { formatSmartRange } from "@/utils";

const formatDays = (days: number) =>
	t("reports.days.count", {
		count: days,
		value: days.toFixed(2).replace(/\.?0+$/, ""),
	});

const Dot: React.FC<{ color?: string }> = ({ color }) =>
	color ? (
		<span
			className="h-2 w-2 shrink-0 rounded-full"
			style={{ background: color }}
		/>
	) : null;

const Fact: React.FC<{ label: string; children: ReactNode }> = ({
	label,
	children,
}) => (
	<div className="min-w-0">
		<dt className="mb-1 text-xs text-neutral-500">{label}</dt>
		<dd className="flex items-center gap-2 font-medium">{children}</dd>
	</div>
);

/**
 * The report's name, and a pencil to rename it in place: the one thing the
 * API still lets change on a finalized report.
 */
const ReportName: React.FC<{ report: TSavedReport }> = ({ report }) => {
	const queryClient = useQueryClient();
	const [draft, setDraft] = useState<string | null>(null);

	const renameMutation = useMutation({
		mutationFn: (name: string) =>
			ReportsServices.update(report.id, { name }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: reportKeys.saved });
			queryClient.invalidateQueries({
				queryKey: reportKeys.savedDetail(report.id),
			});
			setDraft(null);
		},
	});

	const name = draft?.trim() ?? "";

	const submit = (e: FormEvent) => {
		e.preventDefault();
		if (!name || renameMutation.isPending) return;
		if (name === report.name) return setDraft(null);
		promiseToast(renameMutation.mutateAsync(name));
	};

	if (draft === null)
		return (
			<div className="flex min-w-0 items-center gap-2">
				<Title size="h3" className="line-clamp-2">
					<TruncatedTooltipText maxLines={2}>
						{report.name}
					</TruncatedTooltipText>
				</Title>
				<button
					type="button"
					aria-label={t("reports.rename")}
					className="shrink-0 rounded-md p-1 hover:bg-neutral-100"
					onClick={() => setDraft(report.name)}
				>
					<Picto icon="pen" className="w-4 text-black" />
				</button>
			</div>
		);

	return (
		<form
			className="flex min-w-0 items-center gap-2"
			onSubmit={submit}
			onKeyDown={(e) => e.key === "Escape" && setDraft(null)}
		>
			<Input
				className="w-80! max-w-full"
				aria-label={t("reports.submitModal.name")}
				value={draft}
				onChange={setDraft}
				disabled={renameMutation.isPending}
				size="sm"
				autoFocus
			/>
			<Button
				type="submit"
				size="sm"
				picto="check"
				aria-label={t("actions.save")}
				isLoading={renameMutation.isPending}
				disabled={!name}
			/>
			<Button
				type="button"
				size="sm"
				color="white"
				picto="cross"
				aria-label={t("actions.cancel")}
				onClick={() => setDraft(null)}
				disabled={renameMutation.isPending}
			/>
		</form>
	);
};

interface IReportFinalizedHeaderProps {
	report: TSavedReport;
	className?: string;
}

/**
 * A finalized report's head: what it covers, read-only, and what can
 * still be done with it (its PDF, an invoice). Stands in for the filters
 * card, whose selects and "Generate" no longer apply.
 */
export const ReportFinalizedHeader: React.FC<IReportFinalizedHeaderProps> = ({
	report,
	className,
}) => {
	const navigate = useNavigate();
	const { download, isPending } = useReportDownload();

	const days = (report.sections ?? []).reduce(
		(total, section) => total + (section.days || 0),
		0
	);

	return (
		<Card bordered elevation={1} className={className}>
			{/* The actions hold the top-right corner whatever the name's
			    length: the title side shrinks (the name truncates), never
			    the buttons, and nothing wraps under them. */}
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div className="flex flex-1 flex-col gap-1">
					<div className="flex min-w-100 items-center gap-2">
						<ReportName report={report} />
					</div>

					<div className="mt-2 flex items-center gap-2">
						<Badge color="success" variant="tint" size="sm">
							{t("reports.finalizedBadge")}
						</Badge>
						<p className="text-sm text-neutral-500">
							{t("reports.finalizedHint")}
						</p>
					</div>
				</div>
				<div className="ml-auto flex flex-nowrap gap-2 whitespace-nowrap">
					<FeatureGate feature="invoices">
						<PlanGate on="invoices">
							<Button
								color="white"
								picto="fileText"
								onClick={() =>
									navigate(
										`/invoices/new?reportId=${report.id}`
									)
								}
								size="sm"
							>
								{t("invoices.actions.invoice")}
							</Button>
						</PlanGate>
					</FeatureGate>
					<Button
						picto="download"
						size="sm"
						isLoading={isPending}
						onClick={() => download(report.id)}
					>
						{t("reports.downloadPdf")}
					</Button>
				</div>
			</div>

			<Grid className="mt-4">
				{report.client && (
					<Fact label={t("client.label")}>
						<Dot color={report.client.color} />
						<span className="truncate">{report.client.name}</span>
					</Fact>
				)}
				{report.project && (
					<Fact label={t("projects.label")}>
						<Dot color={report.project.color} />
						<span className="truncate">{report.project.name}</span>
					</Fact>
				)}
				<Fact label={t("reports.columns.period")}>
					{formatSmartRange(report.dateFrom, report.dateTo)}
				</Fact>
				<Fact label={t("invoices.form.company")}>
					<span className="truncate">{report.company.name}</span>
				</Fact>
				{report.workType && (
					<Fact label={t("workTypes.label")}>
						<span className="capitalize">
							{t(`workTypes.types.${report.workType}`)}
						</span>
					</Fact>
				)}
				<Fact label={t("reports.billedDuration")}>
					{formatDays(days)}
				</Fact>
			</Grid>
		</Card>
	);
};
